import * as React from 'react';

import * as Images from 'pos/Images';
import * as UI from 'core/ui';

import { NamedDiscountDTO, Res, TicketDTO } from 'core/api';
import { Currency } from 'core/utils';
import { Modal } from 'core/ui-next/modal';
import { BackActionButton, Content, Footer, Header, PhoneOnly, PosScreen, ScrollableGrid } from 'pos/ui';
import { Box, HStack, SegmentGroup, VStack, Text } from '@chakra-ui/react';
import { BasicButton, CardPanel } from 'core/ui-next';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { isPhone } from 'pos/ui/ThemeManager';
import { ProductButton } from '../sale/ProductButton';
import { useTicketNamedDiscount } from './setTicketNamedDiscount';
import { ManualDiscountState, manualDiscountSchema, useTicketManualDiscount } from './setTicketManualDiscount';

const formatMoney = UI.Format.asMoney;

// Las dos pantallas antiguas (descuentos con nombre y descuento manual) pasan a ser las dos pestañas de esta pantalla.
// Cada una sigue teniendo su lógica en su fichero (setTicketNamedDiscount / setTicketManualDiscount), aquí sólo se pinta.
type DiscountTab = 'named' | 'manual';

//Props
type Props = {
	ticket: TicketDTO;
	allDiscounts: NamedDiscountDTO[];
	selectedDiscount: Nullable<NamedDiscountDTO>;
} & Modal.Actions<void>;

function SetTicketDiscountScreen(props: Props) {

	const { ticket, allDiscounts, selectedDiscount, cancel, close } = props;

	// Mismo condicional que hacía el comando para decidir si se abría la pantalla de descuentos con nombre o la de
	// descuento manual. Ahora las dos viven en la misma pantalla: si no hay descuentos con nombre, esa pestaña se
	// deshabilita y sólo se puede aplicar el descuento manual (o cerrar el modal).
	const hasNamedDiscounts = allDiscounts.some(x => x);

	//State
	const [tab, setTab] = React.useState<DiscountTab>(hasNamedDiscounts ? 'named' : 'manual');

	// El formulario del descuento manual se monta aquí y su ref se le pasa al hook, que es quien valida y aplica
	const formRef = React.useRef<FormRef<ManualDiscountState>>(null);
	const { Form, DecimalInput } = useControlsFor<ManualDiscountState>();
	const cashDiscountRef = React.useRef<HTMLInputElement>(null);


	const onCancel = React.useCallback(async () => { cancel(); }, [cancel]);
	const onClose = React.useCallback(() => { close(); }, [close]);


	// Funcionalidad migrada de cada una de las pantallas antiguas
	const { applyDiscount } = useTicketNamedDiscount({ close: onClose });
	const manual = useTicketManualDiscount({ ticket, formRef, close: onClose });


	const onTabChange = React.useCallback((value: Nullable<string>) => {
		if (value !== null)
			setTab(value as DiscountTab);
	}, []);

	const buttonSize = isPhone() ? 'phone-lg' : 'md';

	// Los botones de los descuentos con nombre se pintan con el mismo botón que los productos, respetando el estilo
	// (color, imagen y texto) que llega en el StyleDTO igual que hacía el ValueButtonGrid de la pantalla antigua
	const discountButtons = allDiscounts.map(discount =>
		<ProductButton
			key={discount.Id}
			value={discount}
			size={buttonSize}
			selected={selectedDiscount !== null && discount.Id === selectedDiscount.Id}
			onClick={applyDiscount}
		/>
	);

	// El id de pantalla es sólo para telemetría y logging, así que mantenemos el de la pantalla antigua con la que se abre
	const screenId = hasNamedDiscounts ? 'SET_TICKET_NAMED_DISCOUNT' : 'SET_TICKET_MANUAL_DISCOUNT';

	return <PosScreen screenId={screenId} size={'small'} useKeyPad={true}>
		<Header title={Res.Web.SET_TICKET_DISCOUNTS()} image={Images.SetTicketDiscounts} onClose={onCancel} />
		<Content>
			<VStack
				width={'full'}
				height={'full'}
				paddingX={'6'}
				paddingY={'4.5'}
				gap={'4.5'}
				alignItems={'stretch'}
				bg={{ base: 'white', _dark: 'neutral.900' }}
			>
				{/* Segmentos para cambiar entre los descuentos con nombre y el descuento manual */}
				<HStack width={'full'} justifyContent={'center'}>
					<SegmentGroup.Root
						value={tab}
						onValueChange={(e) => onTabChange(e.value)}
						borderRadius={'md'}
						shadow={'sm'}
						size={'md'}
						css={{
							'--segment-indicator-bg': 'var(--chakra-colors-primary)'
						}}
					>
						<SegmentGroup.Indicator />
						{/* Si el ticket no tiene descuentos con nombre disponibles, esta pestaña no se puede seleccionar */}
						<SegmentGroup.Item value={'named'} disabled={!hasNamedDiscounts}>
							<SegmentGroup.ItemText _checked={{ color: 'white' }}>{Res.Web.SET_TICKET_DISCOUNTS_NAMED_TAB()}</SegmentGroup.ItemText>
							<SegmentGroup.ItemHiddenInput />
						</SegmentGroup.Item>
						<SegmentGroup.Item value={'manual'}>
							<SegmentGroup.ItemText _checked={{ color: 'white' }}>{Res.Web.SET_TICKET_DISCOUNTS_MANUAL_TAB()}</SegmentGroup.ItemText>
							<SegmentGroup.ItemHiddenInput />
						</SegmentGroup.Item>
					</SegmentGroup.Root>
				</HStack>

				{/* named discounts */}
				<VStack
					width={'full'}
					flex={'1'}
					minH={'0'}
					gap={'4'}
					alignItems={'stretch'}
					display={tab === 'named' ? 'flex' : 'none'}
				>
					<Box width={'full'}>
						<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: 'neutral.800', _dark: 'white' }}>
							{Res.Web.SET_TICKET_DISCOUNTS_SELECT_DISCOUNT_LBL()}
						</Text>
					</Box>
					{/* La rejilla crece con su contenido y es este contenedor el que hace el scroll vertical */}
					<VStack width={'full'} flex={'1'} minH={'0'} overflowY={'auto'}>
						<ScrollableGrid items={discountButtons} buttonSize={buttonSize} columnGap={'2'} />
					</VStack>
				</VStack>

				{/* manual discounts. Se mantiene montado al cambiar de pestaña para no perder lo que se haya introducido */}
				<VStack
					width={'full'}
					flex={'1'}
					minH={'0'}
					gap={'4.5'}
					alignItems={'stretch'}
					overflowY={'auto'}
					display={tab === 'manual' ? 'flex' : 'none'}
				>
					<VStack width={'full'} gap={'1'} textAlign={'center'}>
						<Text fontSize={'sm'} fontWeight={'semibold'}>
							{Res.Web.SET_TICKET_DISCOUNTS_ENTER_DISCOUNT_LBL()}
						</Text>
						<Text fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>
							{Res.Web.SET_TICKET_MANUAL_DISCOUNTS_ORIGINAL_AMOUNT()} {formatMoney(manual.originalPrice)}
						</Text>
					</VStack>
					<Form ref={formRef} initialValue={manual.initialState} schema={manualDiscountSchema} style={{ width: '100%' }}>
						<VStack width={'full'} gap={'2'} alignItems={'stretch'}>
							<HStack width={'full'} gap={'4'} alignItems={'flex-start'}>
								<Box flex={'1'}>
									<DecimalInput
										linkTo={'percentageDiscount'}
										allowNegative={false}
										precision={2}
										min={0}
										max={100}
										textAlign={'center'}
										disabled={!manual.canSetPercentageDiscount}
										endElement={<Text fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{'%'}</Text>}
										onWatch={manual.onPercentageDiscountChange}
										onKeyDown={UI.whenEnterKey(() => cashDiscountRef.current?.focus())}
										textAlignLast={'end'}
									/>
								</Box>
								<Box flex={'1'}>
									<DecimalInput
										ref={cashDiscountRef}
										linkTo={'cashDiscount'}
										allowNegative={false}
										precision={Currency.DECIMAL_PLACES}
										min={0}
										textAlign={'center'}
										disabled={!manual.canSetCashDiscount}
										endElement={<Text fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{Currency.SYMBOL}</Text>}
										onWatch={manual.onCashDiscountChange}
										onKeyDown={UI.whenEnterKey(manual.onOK)}
										textAlignLast={'end'}
									/>
								</Box>
							</HStack>
							{/* Mismo aviso que daba el validator de la pantalla antigua cuando los descuentos dejaban el ticket en negativo */}
							{manual.negativeAmountError !== null
								? <Text fontSize={'xs'} color={'error'}>{manual.negativeAmountError}</Text>
								: null}
						</VStack>
					</Form>
					{/* contenido importe final */}
					<CardPanel width={'full'} gap={'1'} alignItems={'stretch'} justifyContent={'flex-start'}>
						<HStack width={'full'} justifyContent={'space-between'}>
							<Text fontSize={'lg'} fontWeight={'semibold'} color={'warning'}>
								{Res.Web.SET_TICKET_MANUAL_DISCOUNTS_TOTAL_AMOUNT()}
							</Text>
							<Text fontSize={'md'} fontWeight={'bold'}>
								{formatMoney(manual.totalAmount)}
							</Text>
						</HStack>
						<Text fontSize={'xs'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>
							{manual.appliedDiscount
								? Res.Web.SET_TICKET_DISCOUNTS_APPLIED_DISCOUNT_LBL(manual.values.percentageDiscount, formatMoney(manual.discountAmount))
								: Res.Web.SET_TICKET_DISCOUNTS_APPLIED_NO_DISCOUNT_LBL()
							}
						</Text>
					</CardPanel>
					<BasicButton type={'success'} text={Res.Web.SET_TICKET_DISCOUNTS_APPLY_BTN()} onClick={manual.onOK} width={'full'} height={'10'} />
				</VStack>
			</VStack>
		</Content>
		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
			</PhoneOnly>
		</Footer>
	</PosScreen >;
}

// Si el ticket no tiene descuentos con nombre disponibles se llama sin descuentos: la pantalla se abre en la pestaña
// del descuento manual y la de descuentos con nombre queda deshabilitada.
export default async function setTicketDiscountScreen(ticket: TicketDTO, allDiscounts: NamedDiscountDTO[] = [], selectedDiscount: Nullable<NamedDiscountDTO> = null): Promise<Result> {
	return Modal.show(SetTicketDiscountScreen, { ticket, allDiscounts, selectedDiscount });
}
